/**
 * Nativity coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_bible_stories`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const nativityColoringPagesPack: ToilePack = {
    slug: 'nativity-coloring-pages',
    category: 'faith',
    name: 'Nativity coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'shepherds', label: 'Shepherds', subject: 'an angel appearing in the night sky over shepherds and their sheep on a hillside outside Bethlehem'},
        {key: 'manger', label: 'The manger', subject: 'baby Jesus in the manger in a stable, Mary and Joseph kneeling beside him, an ox and a donkey looking on'},
        {key: 'magi', label: 'Wise men', subject: 'the three wise men on camels following the bright star toward Bethlehem, carrying their gifts'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'bold thick-outline coloring book style, one heavy uniform black line, big simple open shapes for young children', palette: 'black ink only', framing: 'The drawing centered with a wide white margin on all sides'},
        {key: 'classic', label: 'Classic', style: 'classic coloring book line art, medium uniform black line, moderate detail and open shapes for ages six to nine', palette: 'black ink only', framing: 'The full page used, the drawing centered with a white margin'},
        {key: 'detailed', label: 'Fine detail', style: 'fine-line detailed coloring page, thin uniform black line, patterned robes, leaves and waves for older children', palette: 'black ink only', framing: 'The scene held inside a plain thin rectangular border with a white margin outside it'},
    ],
    directions: ['the inn', 'angels singing', 'snowy night'],
    directionHint: 'try: the inn',
    seo: {
        title: 'Nativity Coloring Pages: Shepherds, Manger, Wise Men',
        metaDescription: 'Nine nativity coloring pages for Sunday school and Christmas from Luke 2 and Matthew 2: the shepherds, the manger and the wise men, in three styles.',
        h1: 'Nativity coloring pages: the shepherds, the manger, the wise men',
        tagline: 'Nine pages of the birth of Jesus in three scenes, each in three line weights.',
        intro: "Printable nativity coloring pages for Sunday school, Christmas children's ministry and Christian homeschool, from Luke 2 and Matthew 2. Three scenes: an angel over the shepherds outside Bethlehem, baby Jesus in the manger with Mary and Joseph, and the three wise men on camels following the star. Bold, classic and fine-detail lines. Type a different scene or detail, and the same styles carry it.",
        useCases: [
            'a Sunday school lesson on the birth of Jesus',
            "a Christmas children's ministry activity",
            'a Christian homeschool Advent unit',
            'a nativity scene activity for a child',
        ],
        faq: [
            {q: 'Which parts of the birth story do these show?', a: 'The shepherds, the manger, and the wise men, from Luke 2 and Matthew 2.'},
            {q: 'Are these only for Christmas?', a: 'No. They suit any Sunday school or homeschool lesson on the birth of Jesus, as well as Christmas.'},
            {q: 'Can I ask for a different setting?', a: 'Yes. Type the setting you want, such as an angel choir or the inn. The request shapes the page, but a result is not promised to match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is black line on white, with open shapes to color in.'},
        ],
    },
}
